import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import {
  FormGroup,
  Label,
} from '@bingads-webui-react/primitive';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Panel, PanelType } from '@bingads-webui-react/fabric-control';
import { Button } from '@bingads-webui-react/primitive-unthemed';
import { ValidationGroup, SelectLite } from '@bingads-webui-react/primitive-validation';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import {
  WithExpressionEditorControls,
  providesDefaults,
  providesProps,
  sharedDefaults,
  sharedProps,
} from '@bingads-webui-react/hoc-with-expression-editor-controls';
import {
  customColumnGridKeyFromId
} from '@bingads-webui-universal/custom-column-utils';
import { isValidForFeature } from '@bingads-webui/custom-columns-shared-config';
import { CustomColumnExpressionEditor } from '@bingads-webui-react/custom-column-expression-editor';
import { setClarityTag } from '@bingads-webui-universal/clarity';

import { CustomColumnFormInput } from './components/input-row';

const validFormats = ['Number', 'Percentage', 'Currency'];
const getStyle = () => ({
  customColumnPanel: {
    '& .instruction': {
      marginTop: '23px',
      marginBottom: '29px',
    },
    '& .error': {
      color: '#A80000',
    },
    '& .ms-Panel-footer': {
      borderTop: '1px solid #EDEBE9',
    },
  },
  inputControls: {
    width: '437px',
  },
  texAreaInput: {
    extend: 'inputControls',
    resize: 'none',
    height: '68px',
  },
  selectorFormGroup: {
    martinTop: '30px',
  },
  saveButton: {
    marginRight: '8px',
  },
});

class FormPanel extends React.PureComponent {
  static propTypes = PropTypes.shape(_.defaults({
    uiDebuggerEnabled: PropTypes.bool,
    i18n: PropTypes.shape({ getString: PropTypes.func }),
    id: PropTypes.string.isRequired,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
    classes: PropTypes.shape().isRequired,
    permissions: PropTypes.shape(),
    /**
     * The form and expression editor contain complex custom controls that
     * need to have their depth explicitly set to be rendered as desired. A
     * convention has been established that allows parent components to control
     * the layering of their child elements:
     *  - 1st layer children: child.zIndex % 1000 === true
     *  - 2nd layer children: may control the zIndex of children, expecting that
     *      child zIndex values stay between child.zIndexStart and child.zIndexStart+1000;
     *      this ensures that the overall layering stays consistent between
     *      children while allowing components to have finer control over layering
     *      within the components.
     */
    zIndexStart: PropTypes.number,
  }, sharedProps, providesProps)).isRequired;

  static defaultProps = _.defaults({
    uiDebuggerEnabled: false,
    i18n: { getString: key => key },
    onCancel: _.noop,
    onSave: _.noop,
    zIndexStart: 10000,
    id: '',
    permissions: {},
  }, sharedDefaults, providesDefaults);

  constructor(props) {
    super(props);
    this.state = { errMsg: undefined };
    setClarityTag({ customColumnPanel: 'true' });
  }

  onValueChange = (event) => {
    this.setState({
      errMsg: null,
    });

    this.props.onFormValueChange(event);
  }

  saveColumn = () => {
    const {
      isFormValid,
      customColumnService,
      isEdit,
      resolvePanel,
      formValueName,
      id,
      formValueDescription,
      expressionString,
      formValueColumnFormat,
      entitySchemaName,
      afterSave,
      onSave,
      i18n,
    } = this.props;

    if (!isFormValid) {
      return;
    }

    const expression = expressionString.split(' ');
    const formData = {
      id,
      name: formValueName,
      description: formValueDescription,
      expression,
      format: formValueColumnFormat,
    };

    customColumnService[isEdit ? 'edit' : 'add'](formData)
      .then((value) => {
        resolvePanel({
          key: customColumnGridKeyFromId(value),
          name: formData.name,
          canEdit: true,
          canDelete: true,
          isValid: isValidForFeature({
            schemaName: entitySchemaName,
            expression,
            permissions: this.props.permissions,
          }),
        });

        formData.id = value;
        onSave();
        afterSave(formData);
      }).catch((data) => {
        const { errors } = data;
        const error = _.first(errors);
        let errMsg;

        if (error) {
          if (error.Code === 'CustomColumnIdIsInvalid') {
            errMsg = i18n.getString('CustomColumn_DoesNotExist');
            afterSave({ id: undefined });
          } else if ((error.Code === 'AccountCustomColumnLimitExceeded') ||
            (error.Code === 'CustomerCustomColumnLimitExceeded')) {
            errMsg = i18n.getString('CustomColumn_LimitExceeded');
          } else if (error.Code === 'CustomColumnExpressionIsTooLong') {
            errMsg = i18n.getString('CustomColumnExpressionEditor_Expression_Too_Long');
          } else {
            errMsg = i18n.getString(`${error.Property}_${error.Code}`);
          }
        }

        this.setState({
          errMsg,
        });
      });
  };

  render() {
    const {
      i18n,
      customColumnFormSchema: validationSchema,
      customColumnFormErrorMessages,
      customColumnFormIsDirty,
      classes,
    } = this.props;

    const renderFooterContent = () => (
      <div className="footer">
        <Button
          btnStyle="primary"
          className={classNames('save', classes.saveButton)}
          disabled={!this.props.isFormValid}
          onClick={this.saveColumn}
        >
          {i18n.getString('CustomColumn_Save')}
        </Button>
        <Button className="cancel" onClick={this.props.onCancel}>
          {i18n.getString('CustomColumn_Cancel')}
        </Button>
      </div>
    );
    const nameLabelId = 'custom-column-name-label';
    const descriptionLabelId = 'custom-column-description-label';
    const formantLabelId = 'custom-column-formant-label';

    return (
      <Panel
        id="custom-column-modal"
        isOpen="true"
        headerText={i18n.getString('CustomColumn_CreateColumnTitle')}
        onDismiss={this.props.onCancel}
        closeButtonAriaLabel={i18n.getString('CustomColumn_Cancel')}
        type={PanelType.custom}
        customWidth="973px"
        onRenderFooterContent={renderFooterContent}
        onOuterClick={() => {}}
        isFooterAtBottom
        className={classes.customColumnPanel}
      >
        <div className="panel-content">
          <ValidationGroup
            validationProps={{
                i18n,
                schema: validationSchema,
                onValidate: this.props.onFormValidate,
              }}
          >
            <div className="instruction" role="heading" aria-level="3">
              {i18n.getString('CustomColumn_Description')}
            </div>
            <CustomColumnFormInput
              i18n={i18n}
              name="name"
              inputClassName={classes.inputControls}
              inputLabelId={nameLabelId}
              maxLength={validationSchema.properties.name.maxLength}
              inputValue={this.props.formValueName}
              onInputChange={this.onValueChange}
              isFormDirty={customColumnFormIsDirty}
              formErrorMessages={customColumnFormErrorMessages}
              helpTopic="pop_BA_Campaign_CustomCol_Name"
              helpLabel={i18n.getString('CustomColumn_NameHelpLabel')}
            />
            <CustomColumnFormInput
              i18n={i18n}
              name="description"
              inputClassName={classes.texAreaInput}
              inputLabelId={descriptionLabelId}
              maxLength={validationSchema.properties.description.maxLength}
              inputValue={this.props.formValueDescription}
              onInputChange={this.onValueChange}
              isFormDirty={customColumnFormIsDirty}
              formErrorMessages={customColumnFormErrorMessages}
              isTextArea
              helpTopic="pop_BA_Campaign_CustomCol_Desc"
              helpLabel={i18n.getString('CustomColumn_DescriptionHelpLabel')}
            />
            <CustomColumnExpressionEditor
              id="custom-column-expression"
              i18n={i18n}
              zIndexStart={this.props.zIndexStart + 1000}
              metricCategories={this.props.metricCategories}
              segmentCategories={this.props.segmentCategories}
              {..._.pick(this.props, _.keys(providesProps))}
            />
            <FormGroup className={classes.selectorFormGroup}>
              <Label id={formantLabelId}>
                {i18n.getString('CustomColumn_ColumnFormatLabel')}
                <HelpPopup
                  aria-label={i18n.getString('CustomColumn_FormatHelpLabel')}
                  topic="pop_BA_Campaign_CustomCol_Format"
                />
              </Label>
              <SelectLite
                id="custom-column-format"
                name="columnFormat"
                aria-labelledby={formantLabelId}
                className={classes.inputControls}
                onChange={this.onValueChange}
                style={{ zIndex: this.props.zIndexStart + 10 }}
                value={this.props.formValueColumnFormat}
                validationProps={{ field: 'columnFormat' }}
              >
                {validFormats.map(format => (
                  <option key={format} value={format}>
                    {i18n.getString(`CustomColumn_ColumnFormat_${format}`)}
                  </option>
                        ))}
              </SelectLite>
              <div className="validation-feedback-todo" />
            </FormGroup>
          </ValidationGroup>
          {this.props.uiDebuggerEnabled ? (
            <pre>
              {JSON.stringify({
                debugInfo: {
                  customColumnFormIsDirty: this.props.customColumnFormIsDirty,
                  isFormValid: this.props.isFormValid,
                  expression: this.props.expressionString,
                  customColumnFormErrorMessages: this.props.customColumnFormErrorMessages,
                },
              }, null, 2)}
            </pre>
          ) : null }
          {
            this.state.errMsg ? (
              <div role="alert">
                <div className="error">{this.state.errMsg}</div>
              </div>
            ) : null}
        </div>
      </Panel>
    );
  }
}

export const CustomColumnFormPanel =
      WithExpressionEditorControls(withDefaultStyles(FormPanel, getStyle));
